import { colors } from '@globals/themes/defaultTheme'
import { onHover, semiBoldFontWeight } from '@globals/themes/themeConstants'
import useMemberProfileRoute from '@globals/useMemberProfileRoute'
import Box from '@mui/material/Box'
import { SxProps } from '@mui/material/styles'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import HtmlContent from '../HtmlContent'
import MentionsPopper from './MentionsPopper'

export const HtmlContentStyle: SxProps = {
  overflowWrap: 'anywhere',
  '& span[data-type="mention"]': {
    fontWeight: semiBoldFontWeight,
    color: colors.bluePrimary,
    [onHover]: {
      cursor: 'pointer',
    },
  },
  '& > p': {
    marginBlockStart: 0,
  },
  '& > p:last-child': {
    marginBlockEnd: 0,
  },
}

type HtmlContentWithMentionsProps = {
  htmlContent: string
  height?: number | string
  maxHeight?: number | string
}

const HtmlContentWithMentions: React.FC<HtmlContentWithMentionsProps> = ({
  htmlContent,
  height = '100%',
  maxHeight = '100%',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [tenantMemberId, setTenantMemberId] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const getMemberProfileRoute = useMemberProfileRoute()

  const handleMouseOver = (event: Event) => {
    event.stopPropagation()
    const target = event.target as HTMLElement
    if (target.matches('[data-type="mention"]')) {
      const mentionId = target.getAttribute('data-id')
      if (mentionId) {
        setAnchorEl(target)
        setTenantMemberId(mentionId)
      }
    }
  }

  const handleMouseOut = (event: Event) => {
    event.stopPropagation()
    const target = event.target as HTMLElement
    if (target.matches('[data-type="mention"]')) {
      setAnchorEl(null)
      setTenantMemberId(null)
    }
  }

  const handleMouseClick = (event: Event) => {
    event.stopPropagation()
    const target = event.target as HTMLElement
    if (target.matches('[data-type="mention"]')) {
      const mentionId = target.getAttribute('data-id')
      if (mentionId) {
        navigate(getMemberProfileRoute(mentionId))
      }
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    // Add bubbling event listeners to html ref containing mentions
    const mentions = container.querySelectorAll('[data-type="mention"]')
    mentions.forEach(mention => {
      mention.addEventListener('mouseover', handleMouseOver)
      mention.addEventListener('mouseout', handleMouseOut)
      mention.addEventListener('click', handleMouseClick)
    })

    // Clean up on unmount, remove event listeners
    return () => {
      mentions.forEach(mention => {
        mention.removeEventListener('mouseover', handleMouseOver)
        mention.removeEventListener('mouseout', handleMouseOut)
        mention.removeEventListener('click', handleMouseClick)
      })
    }
  }, [handleMouseOver, handleMouseOut])

  return (
    <Box ref={containerRef} sx={{ height, overflow: 'auto', maxHeight }}>
      <HtmlContent htmlContent={htmlContent} sx={HtmlContentStyle} />
      {tenantMemberId && (
        <MentionsPopper
          tenantMemberId={tenantMemberId}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
        />
      )}
    </Box>
  )
}

export default HtmlContentWithMentions
